<template>
  <PageView :main-title="$t(`ParametersPage.title`)" :sub-title="$t(`ParametersPage.subtitle`)">
    <ContentView>
      <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
        <H3TitleView class="mb-2">{{ $t("ParametersPage.inscription.title") }}</H3TitleView>
        <b-message type="is-info">{{ $t("ParametersPage.national-message") }}</b-message>
        <DatePickerWithValidation
          v-model="parametrage.debutInscription"
          vid="date1"
          rules="required"
          :label="$t('ParametersPage.inscription.date-ouverture')"
          :placeholder="$t('ParametersPage.selectionner-date')"
          :disabled="isObservateurNational"
        />
        <DatePickerWithValidation
          v-model="parametrage.finInscription"
          vid="date2"
          :rules="`${
            parametrage.debutInscription
              ? `dateIsAfter:${parametrage.debutInscription.toString()}`
              : ''
          }|required`"
          :label="$t('ParametersPage.inscription.date-fermeture')"
          :placeholder="$t('ParametersPage.selectionner-date')"
          :disabled="isObservateurNational"
        />

        <DatePickerWithValidation
          v-model="parametrage.debutLabellisation"
          vid="date3"
          rules="required"
          :label="$t('ParametersPage.labellisation.date-ouverture')"
          :placeholder="$t('ParametersPage.selectionner-date')"
          :disabled="isObservateurNational"
        />
        <DatePickerWithValidation
          v-model="parametrage.finLabellisation"
          vid="date4"
          :rules="`${
            parametrage.debutLabellisation
              ? `dateIsAfter:${parametrage.debutLabellisation.toString()}`
              : ''
          }|required`"
          :label="$t('ParametersPage.labellisation.date-fermeture')"
          :placeholder="$t('ParametersPage.selectionner-date')"
          :disabled="isObservateurNational"
        />

        <H3TitleView class="mb-2">{{ $t("ParametersPage.periode-scolaire.title") }}</H3TitleView>
        <b-message type="is-info">{{ $t("ParametersPage.periode-scolaire.message") }}</b-message>
        <InputWithValidation
          v-model="debutAnneeScolaire"
          :label="$t(`ParametersPage.debut-annee-scolaire`)"
          :disabled="true"
          rules="required"
          vid="debutAnneeScolaire"
          type="text"
        />
        <div v-if="!isObservateurNational" class="buttons mt-4 mb-4 ParametersPage-buttons">
          <b-button type="is-primary" @click="handleSubmit(submit)">
            {{ $t("ParametersPage.valider-dates") }}
          </b-button>
        </div>
      </ValidationObserver>
    </ContentView>
  </PageView>
</template>

<script lang="ts">
import { BMessage, BButton } from "buefy"

import { defineComponent } from "vue"
import PageView from "@/views/commons/PageView.vue"
import ContentView from "@/views/commons/ContentView.vue"
import DatePickerWithValidation from "@/views/inputs/DatePickerWithValidation.vue"
import { ParametrageDTO } from "@/model/bean/GeneratedDTOs"
import { ParametersService } from "@/services/rest/parameters/ParametersService"
import { LoginService } from "@/services/rest/login/LoginService"
import { UsersService } from "@/services/rest/users/UsersService"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import H3TitleView from "@/views/commons/H3TitleView.vue"

export default defineComponent({
  name: "ParametersPage",
  components: {
    PageView,
    ContentView,
    DatePickerWithValidation,
    ValidationObserver,
    InputWithValidation,
    H3TitleView,
    BButton,
    BMessage,
  },
  data() {
    return {
      parametersService: ParametersService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      loginService: LoginService.INSTANCE,
      usersService: UsersService.INSTANCE,
      isObservateurNational: false,
      debutAnneeScolaire: "",
      parametrage: new ParametrageDTO(),
    }
  },
  created(): void {
    this.init()

    const loggedUser = this.loginService.getLoggedUser()
    if (loggedUser) {
      this.isObservateurNational = this.usersService.isObservateurNational(loggedUser)
    }
  },
  methods: {
    async init(): Promise<void> {
      try {
        this.parametrage = await this.parametersService.getParametrage()
        // Conversion factorisée des chaînes de caractères en objets Date
        ;["debutInscription", "finInscription", "debutLabellisation", "finLabellisation"].forEach(
          (key: string) => {
            const value = (this.parametrage as any)[key]
            if (value && typeof value === "string") {
              ;(this.parametrage as any)[key] = new Date(value)
            }
          },
        )

        this.debutAnneeScolaire = `${this.parametrage.jourDebutAnneeScolaire} ${this.parametrage.moisDebutAnneeScolaire}`
      } catch (e: any) {
        console.warn("[WARNING] ", e)
      }
    },
    async submit(): Promise<void> {
      try {
        await this.parametersService.createParametrage(this.parametrage)
        this.infoReportingService.success(this.$t("ParametersPage.modification-ok").toString())
      } catch (e: any) {
        this.infoReportingService.error(
          this.$t("ParametersPage.modification-error").toString(),
          e as Error,
        )
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.ParametersPage-buttons.buttons {
  justify-content: flex-end;
}
</style>
